import {
    DraftCompleted,
    DraftDetail,
    DraftKey,
    DraftSaveRequest,
    DraftShare,
    DraftShareTarget,
    DraftType,
} from 'types/drafts';
import { objectToQueryParams } from 'utils/linkUtil';
import { portalAPI } from './portalAPI';

/** The lists, and their counts, a draft of each type shows in: a write to a draft changes what they hold. */
const LIST_TAGS: Record<DraftType, string[]> = {
    case: ['MattersListItem', 'MattersCount'],
    contact: ['Contacts', 'ContactsCount'],
    contract: ['ContractsListItem', 'ContractsCount'],
    'client-asset': ['PartyAssetListItems', 'PartyAssetsCount'],
    expense: ['ExpensesListItem', 'ExpensesCount'],
    task: ['TaskListItems'],
    'bd-effort': ['EffortListItem', 'EffortsCount'],
    project: ['ProjectListItems'],
    training: ['TrainingListItems'],
    debriefing: ['DebriefingListItems', 'DebriefingsCount'],
    'marketing-profile': ['MarketingProfiles', 'MarketingProfilesCount'],
    employee: ['Employees'],
    group: ['PartyGroupListItems'],
    team: ['Teams'],
    office: ['Offices'],
    'practice-area': ['PracticeAreas'],
    'job-role': ['JobRoles'],
    'job-position': ['JobPositions'],
};

const draftTags = (type: DraftType, id?: number) => [
    'Drafts' as const,
    ...(id === undefined ? [] : [{ type: 'Draft' as const, id: `${type}/${id}` }]),
    ...LIST_TAGS[type],
];

export const APIDraftsSlice = portalAPI
    .enhanceEndpoints({ addTagTypes: ['Drafts', 'Draft', ...new Set(Object.values(LIST_TAGS).flat())] })
    .injectEndpoints({
        endpoints: (builder) => ({
            /** Every draft the caller sees, latest save first. */
            getDrafts: builder.query<DraftDetail[], void>({
                query: () => '/v1/drafts',
                providesTags: ['Drafts'],
            }),
            getDraft: builder.query<DraftDetail, DraftKey>({
                query: ({ type, id }) => `/v1/drafts/${type}/${id}`,
                providesTags: (_1, _2, { type, id }) => [{ type: 'Draft', id: `${type}/${id}` }],
            }),
            getDraftShareTargets: builder.query<DraftShareTarget[], string>({
                query: (search) => `/v1/drafts/share-targets${objectToQueryParams({ search })}`,
            }),
            createDraft: builder.mutation<DraftDetail, DraftSaveRequest>({
                query: ({ type, payload, missingFields }) => ({
                    url: `/v1/drafts/${type}`,
                    method: 'POST',
                    body: { payload, missingFields },
                }),
                invalidatesTags: (_1, _2, { type }) => draftTags(type),
            }),
            saveDraft: builder.mutation<DraftDetail, DraftSaveRequest & { id: number }>({
                query: ({ type, id, payload, missingFields }) => ({
                    url: `/v1/drafts/${type}/${id}`,
                    method: 'PUT',
                    body: { payload, missingFields },
                }),
                invalidatesTags: (_1, _2, { type, id }) => draftTags(type, id),
            }),
            /** Creates the record the draft holds, under every rule of its type: it is a draft no more. */
            completeDraft: builder.mutation<DraftCompleted, DraftKey & { payload: unknown }>({
                query: ({ type, id, payload }) => ({
                    url: `/v1/drafts/${type}/${id}/complete`,
                    method: 'POST',
                    body: { payload },
                }),
                // Not the draft's own tag: the form completing it still reads it, and a draft no more reads as a 404.
                invalidatesTags: (_1, _2, { type }) => draftTags(type),
            }),
            shareDraft: builder.mutation<
                DraftDetail,
                DraftKey & { shares: Pick<DraftShare, 'type' | 'id' | 'right'>[] }
            >({
                query: ({ type, id, shares }) => ({
                    url: `/v1/drafts/${type}/${id}/shares`,
                    method: 'PUT',
                    body: { shares },
                }),
                invalidatesTags: (_1, _2, { type, id }) => draftTags(type, id),
            }),
            deleteDraft: builder.mutation<void, DraftKey>({
                query: ({ type, id }) => ({ url: `/v1/drafts/${type}/${id}`, method: 'DELETE' }),
                invalidatesTags: (_1, _2, { type, id }) => draftTags(type, id),
            }),
        }),
    });

export const {
    useGetDraftsQuery,
    useGetDraftQuery,
    useGetDraftShareTargetsQuery,
    useCreateDraftMutation,
    useSaveDraftMutation,
    useCompleteDraftMutation,
    useShareDraftMutation,
    useDeleteDraftMutation,
} = APIDraftsSlice;
